import { ICreateManualQuotation, ILogisticServiceWithProductPrices } from 'types/services';
import { ILogisticServiceEditionForm } from 'views/LogisticServices/LogisticServiceEdition/LogisticServiceEdition.types';

export const buildCurrentServices = (
  data: ILogisticServiceEditionForm,
  LSData?: ILogisticServiceWithProductPrices
): ICreateManualQuotation['items'] =>
  data.products.flatMap(
    (product, productIndex) =>
      product.logisticServiceProductPrices?.map((service, serviceIndex) => ({
        productId: product.productId || undefined,
        logisticServiceTypeId: service.logisticServiceTypeId || service.logisticServiceType?.id || undefined,
        logisticServiceProductPriceId: service.id || undefined,
        currency: String(data.currency) || 'USD',
        exchangeRate: Number(service.exchangeRate) || 1,
        priceUsd: Number(service.priceUsd) || 0,
        taxValueUsd: Number(service.taxValueUsd) || 0,
        totalValueUsd: Number(service.totalValueUsd) || 0,
        coveredByCare: service.coveredByCare || false,
        previousUnitAmount:
          Number(
            LSData?.products?.[productIndex]?.logisticServiceProductPrices?.find(
              currService => service.id === currService.id
            )?.totalValueUsd
          ) || 0,
        newUnitAmount: Number(service.totalValueUsd) || 0
      })) || []
  ) || [];

export const buildDeletedServices = (
  currentServices: ICreateManualQuotation['items'],
  data: ILogisticServiceEditionForm,
  LSData?: ILogisticServiceWithProductPrices
): ICreateManualQuotation['items'] =>
  LSData?.products
    ?.flatMap(product => product.logisticServiceProductPrices || [])
    .filter(service => !currentServices.find(item => item.logisticServiceProductPriceId === service.id))
    .map(service => ({
      productId: undefined,
      logisticServiceTypeId: service.logisticServiceTypeId || service.logisticServiceType?.id || undefined,
      logisticServiceProductPriceId: service.id || undefined,
      currency: String(data.currency) || 'USD',
      exchangeRate: Number(service.exchangeRate) || 1,
      priceUsd: Number(service.priceUsd) || 0,
      taxValueUsd: Number(service.taxValueUsd) || 0,
      totalValueUsd: Number(service.totalValueUsd) || 0,
      coveredByCare: service.coveredByCare || false,
      previousUnitAmount: Number(service.totalValueUsd) || 0,
      newUnitAmount: null
    })) || [];

export const buildPriceEditionPayload = (
  data: ILogisticServiceEditionForm,
  LSData?: ILogisticServiceWithProductPrices
): ICreateManualQuotation => {
  const currentServices = buildCurrentServices(data, LSData);
  const deletedServices = buildDeletedServices(currentServices, data, LSData);

  return {
    logisticServiceId: LSData?.id,
    currency: data.currency,
    previousTotalAmount: Number(LSData?.logisticServicePrice?.totalUsd),
    newTotalAmount: data.totalUsd,
    reason: data.reason,
    changedAt: new Date().toISOString(),
    needInvoice: true,
    previousQuotationUrl: LSData?.quoteDocumentUrl,
    commissionUsd: data.commissionUsd,
    exchangeRate: Number(data.exchangeRate) || 1,
    items: [...currentServices, ...deletedServices]
  };
};

export const initializePriceEditionForm = (
  LSData?: ILogisticServiceWithProductPrices
): ILogisticServiceEditionForm => ({
  products: LSData?.products || [],
  currency: LSData?.logisticServicePrice?.currency || 'USD',
  exchangeRate: Number(LSData?.logisticServicePrice?.exchangeRate) || 0,
  subtotal: Number(LSData?.logisticServicePrice?.totalUsd) || 0,
  totalUsd: Number(LSData?.logisticServicePrice?.totalUsd) || 0,
  totalLocal:
    Number(LSData?.logisticServicePrice?.totalLocal) ||
    Number(Number(LSData?.logisticServicePrice?.totalUsd) * Number(LSData?.logisticServicePrice?.exchangeRate)) ||
    0,
  iva: Number(LSData?.logisticServicePrice?.totalTaxesUsd || 0),
  commissionUsd:
    (Number(LSData?.logisticServicePrice?.commissionUsd) || 0) +
    (Number(LSData?.logisticServicePrice?.commissionTaxUsd) || 0),
  levelSubscription: LSData?.organizationData?.platformSubscription || undefined
});

export const EDITION_FORM_DEFAULT_VALUES: ILogisticServiceEditionForm = {
  products: [],
  exchangeRate: 0,
  currency: 'USD',
  subtotal: 0,
  totalUsd: 0,
  totalLocal: 0,
  iva: 0,
  reason: undefined,
  commissionUsd: 0
};
